import {
	type CreateOrderQuoteOfferBatchAdjustmentInput,
	type CreateOrderQuoteOfferBatchSampleInput,
	useAddOffersToOrderQuoteOfferBatchMutation,
	useCreateOrderQuoteOfferBatchMutation,
	SamplePaymentTreatment,
} from '@codezo/graphql';
import { Check, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AnimatedHeight } from '../../ui/animated-height';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { cn } from '../../../lib/utils';
import { useCurrencyStore } from '../../../stores/currency';
import { deliveryDayOrNull, deliveryMoney, isDeliveryOffered } from './delivery-rules';
import type { PricingValues, QuoteItem, Step } from './wizard/types';
import { pricingPayload } from '../../pricing/tier-pricing';
import { emptyPricing, hasPricingData } from './wizard/types';
import { WizardInfoStep } from './wizard/WizardInfoStep';
import { WizardProductsStep } from './wizard/WizardProductsStep';
import { WizardPricingStep } from './wizard/WizardPricingStep';
import { WizardDeliveryStep } from './wizard/WizardDeliveryStep';

/* ── Props ── */

interface CreateOfferBatchWizardProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	quoteId: string;
	quoteItems: QuoteItem[];
	/** Quote-level deadline, used as the feasibility fallback when an item has none. */
	quoteDeadline?: string | null;
	onSuccess: () => void;
	/** When set, adds products to an existing offer instead of creating a new one */
	existingBatchId?: string;
	/** Quote item IDs already in the batch — excluded from product selection */
	existingOfferItemIds?: string[];
}

/* ── Step configs ── */

/* Delivery comes last because a delivery hangs off a priced quantity — the step
   can only offer the tiers that pricing gave a price to. */
const STEPS_CREATE: { key: Step; label: string }[] = [
	{ key: 'info', label: 'Offer info' },
	{ key: 'products', label: 'Products' },
	{ key: 'pricing', label: 'Pricing' },
	{ key: 'delivery', label: 'Delivery' },
];

const STEPS_ADD: { key: Step; label: string }[] = [
	{ key: 'products', label: 'Products' },
	{ key: 'pricing', label: 'Pricing' },
	{ key: 'delivery', label: 'Delivery' },
];

/* ── Wizard ── */

export function CreateOfferBatchWizard({
	open,
	onOpenChange,
	quoteId,
	quoteItems,
	quoteDeadline,
	onSuccess,
	existingBatchId,
	existingOfferItemIds = [],
}: CreateOfferBatchWizardProps) {
	const isAddMode = !!existingBatchId;
	const STEPS = isAddMode ? STEPS_ADD : STEPS_CREATE;
	const [step, setStep] = useState<Step>(isAddMode ? 'products' : 'info');

	// Step 1: Offer info (skip in add mode)
	const [acceptDeadline, setAcceptDeadline] = useState<Date | null>(null);

	// Per-offer vetter assignment (keyed by quote item ID)
	const [vetters, setVetters] = useState<Record<string, string>>({});

	// Step 2: Product selection
	const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

	// Step 3: Pricing — per quantity tier
	const [pricing, setPricing] = useState<Record<string, Record<string, PricingValues>>>({});
	const { selectedCurrency: currency } = useCurrencyStore();

	// Step 3: Active product in sidebar
	const [activeItemId, setActiveItemId] = useState<string | null>(null);
	const [activeQuantityId, setActiveQuantityId] = useState<string | null>(null);

	// Adjustments per product (itemId -> adjustments sent inline with createBatch)
	const [adjustments, setAdjustments] = useState<
		Record<string, CreateOrderQuoteOfferBatchAdjustmentInput[]>
	>({});

	// Sample per product (itemId -> sample config)
	const [samples, setSamples] = useState<Record<string, CreateOrderQuoteOfferBatchSampleInput>>(
		{}
	);

	// Custom quantities per product (itemId -> extra quantities not from the quote)
	const [customQuantities, setCustomQuantities] = useState<
		Record<string, Array<{ id: string; quantity: number }>>
	>({});

	const [createBatch] = useCreateOrderQuoteOfferBatchMutation();
	const [addOffersToBatch] = useAddOffersToOrderQuoteOfferBatchMutation();
	const [saving, setSaving] = useState(false);

	/* ── Reset ── */

	const reset = () => {
		setStep(isAddMode ? 'products' : 'info');
		setAcceptDeadline(null);
		setVetters({});
		setSelectedItems(new Set());
		setPricing({});
		setActiveItemId(null);
		setActiveQuantityId(null);
		setAdjustments({});
		setSamples({});
		setCustomQuantities({});
	};

	const handleClose = (next: boolean) => {
		if (!next) reset();
		onOpenChange(next);
	};

	/* ── Navigation helpers ── */

	const toggleItem = (id: string) => {
		setSelectedItems((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};

	const goToProducts = () => {
		setStep('products');
	};

	const goToPricing = () => {
		if (selectedItems.size === 0) return;
		const initial: Record<string, Record<string, PricingValues>> = {};
		for (const itemId of selectedItems) {
			const item = quoteItems.find((i) => i.id === itemId);
			if (item) {
				initial[itemId] = {};
				for (const q of item.quantities) {
					initial[itemId][q.id] = pricing[itemId]?.[q.id] || emptyPricing;
				}
			}
		}
		setPricing(initial);
		const firstItemId = Array.from(selectedItems)[0];
		setActiveItemId(firstItemId);
		const firstItem = quoteItems.find((i) => i.id === firstItemId);
		setActiveQuantityId(firstItem?.quantities[0]?.id ?? null);
		setStep('pricing');
	};

	const goToDelivery = () => setStep('delivery');

	/* ── Save ── */

	const handleSave = async () => {
		setSaving(true);
		try {
			const offers = Array.from(selectedItems).map((itemId) => {
				const item = quoteItems.find((i) => i.id === itemId);
				const itemPricing = pricing[itemId];

				// A tier is sent when it carries anything at all — a price, production
				// days, a margin, or a delivery. Shipping no longer depends on the
				// tier having a price: an unpriced tier that carries a delivery is
				// sent at 0. Tiers the admin never touched are still left out.
				const pricingItems = itemPricing
					? Object.entries(itemPricing)
							.filter(([, val]) => hasPricingData(val))
							.map(([quantityId, val]) => ({
								orderQuoteItemQuantityId: quantityId,
								// Sends the sale price or the markup — whichever the admin
								// typed. They are mutually exclusive on the input.
								...pricingPayload({
									cost: val.price,
									salePrice: val.salePrice,
									markup: val.markup,
									entryMode: val.entryMode,
									currency,
								}),
								minProductionDays: val.minDays ? parseInt(val.minDays) : undefined,
								maxProductionDays: val.maxDays ? parseInt(val.maxDays) : undefined,
								// The price is what makes a delivery row exist — the days
								// are nullable, so a method priced before the forwarder
								// answered is sent as it stands instead of being dropped.
								// A method that still can't be written held up this step.
								deliveries: val.deliveries.some(isDeliveryOffered)
									? val.deliveries.filter(isDeliveryOffered).map((d) => ({
											shippingMethod: d.shippingMethod,
											price: deliveryMoney(d.price),
											currency,
											deliveryDaysMin: deliveryDayOrNull(d.deliveryDaysMin),
											deliveryDaysMax: deliveryDayOrNull(d.deliveryDaysMax),
											quantity:
												item?.quantities.find((q) => q.id === quantityId)
													?.quantity ?? 0,
										}))
									: undefined,
							}))
					: [];

				const itemAdjustments = adjustments[itemId];

				return {
					name: item?.productName || 'Offer',
					orderQuoteItemId: itemId,
					vetterEntityId: vetters[itemId] || undefined,
					pricing: pricingItems.length > 0 ? pricingItems : undefined,
					adjustments: itemAdjustments?.length
						? itemAdjustments.filter((a) => a.name.trim() && a.value > 0)
						: undefined,
					// A waived sample has no price by design, so presence can't be
					// inferred from one any more — it would silently drop every
					// giveaway on the way to the API.
					sample:
						samples[itemId]?.baseSamplePrice ||
						samples[itemId]?.paymentTreatment === SamplePaymentTreatment.Waived
							? samples[itemId]
							: undefined,
				};
			});

			if (isAddMode) {
				await addOffersToBatch({
					variables: {
						id: existingBatchId!,
						offers,
					},
				});
			} else {
				await createBatch({
					variables: {
						input: {
							orderQuoteId: quoteId,
							acceptDeadline: acceptDeadline?.toISOString() || undefined,
							offers,
						},
					},
				});
			}

			toast.success(isAddMode ? 'Products added' : 'Offer created');
			handleClose(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create offer');
		} finally {
			setSaving(false);
		}
	};

	/* ── Render ── */

	const currentStepIdx = STEPS.findIndex((s) => s.key === step);

	return (
		<>
			<Dialog open={open} onOpenChange={handleClose} disablePointerDismissal>
				<DialogContent
					className={cn(
						step === 'info' && 'sm:max-w-lg',
						step === 'products' && 'sm:max-w-xl',
						(step === 'pricing' || step === 'delivery') && 'sm:max-w-4xl'
					)}
				>
					<DialogHeader className="pb-3">
						<DialogTitle>{isAddMode ? 'Add products' : 'New offer'}</DialogTitle>
						{/* Step indicator */}
						<div className="flex items-center gap-1.5 pt-2">
							{STEPS.map((s, i) => (
								<div key={s.key} className="flex items-center gap-1.5">
									{i > 0 && <ChevronRight size={12} className="text-text-4" />}
									<span
										className={cn(
											'text-sm transition-colors',
											i === currentStepIdx
												? 'font-medium text-text-1'
												: i < currentStepIdx
													? 'text-accent'
													: 'text-text-4'
										)}
									>
										{i < currentStepIdx && (
											<Check
												size={11}
												className="mr-0.5 inline text-accent"
											/>
										)}
										{s.label}
									</span>
								</div>
							))}
						</div>
					</DialogHeader>

					{/* Step 1: Offer info */}
					{step === 'info' && (
						<AnimatedHeight step="info">
							<WizardInfoStep
								acceptDeadline={acceptDeadline}
								setAcceptDeadline={setAcceptDeadline}
								onNext={goToProducts}
								onCancel={() => handleClose(false)}
							/>
						</AnimatedHeight>
					)}

					{/* Step 2: Select products */}
					{step === 'products' && (
						<AnimatedHeight step="products">
							<WizardProductsStep
								quoteItems={quoteItems}
								selectedItems={selectedItems}
								toggleItem={toggleItem}
								existingOfferItemIds={existingOfferItemIds}
								onBack={() => setStep('info')}
								onNext={goToPricing}
								onCancel={() => handleClose(false)}
								isAddMode={isAddMode}
							/>
						</AnimatedHeight>
					)}

					{/* Step 3: Pricing */}
					{step === 'pricing' && (
						<AnimatedHeight step="pricing">
							<WizardPricingStep
								quoteItems={quoteItems}
								selectedItems={selectedItems}
								quoteDeadline={quoteDeadline ?? null}
								acceptDeadline={acceptDeadline?.toISOString() ?? null}
								pricing={pricing}
								setPricing={setPricing}
								vetters={vetters}
								setVetters={setVetters}
								adjustments={adjustments}
								setAdjustments={setAdjustments}
								samples={samples}
								setSamples={setSamples}
								customQuantities={customQuantities}
								activeItemId={activeItemId}
								setActiveItemId={setActiveItemId}
								activeQuantityId={activeQuantityId}
								setActiveQuantityId={setActiveQuantityId}
								onBack={() => setStep('products')}
								onNext={goToDelivery}
							/>
						</AnimatedHeight>
					)}

					{/* Step 4: Delivery */}
					{step === 'delivery' && (
						<AnimatedHeight step="delivery">
							<WizardDeliveryStep
								quoteItems={quoteItems}
								selectedItems={selectedItems}
								customQuantities={customQuantities}
								pricing={pricing}
								setPricing={setPricing}
								quoteDeadline={quoteDeadline ?? null}
								acceptDeadline={acceptDeadline?.toISOString() ?? null}
								onBack={() => setStep('pricing')}
								onSave={handleSave}
								saving={saving}
								isAddMode={isAddMode}
							/>
						</AnimatedHeight>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
